import React, { useEffect, useState } from 'react';
import { PriceAnalysisChartWrapper } from './PriceAnalysisChartWrapper';
import { MarketBiddingVolumeChartWrapper } from './MarketBiddingVolumeChartWrapper';
import { ConsumptionGenerationChartWrapper } from './ConsumptionGenerationChartWrapper';
import { CoalStockPriceChartWrapper } from './CoalStockPriceChartWrapper';
import { FullscreenModal } from '../../components/FullscreenModal';
import { COLOR_CODES } from '../../common';
import Loading from '../../components/Loading';
import { getLatestDataPriceForecastDate } from '../../Rest_api/RestApi';

export function MarketDerivative({startDate, endDate}: {startDate: string, endDate: string}) {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStates, setLoadingStates] = useState({
        card1: false,
        card2: false,
        card3: false,
        card4: false
    });
    const [fullscreenCard, setFullscreenCard] = useState<string | null>(null);
    const [fullscreenStartDate, setFullscreenStartDate] = useState<string>(startDate);
    const [fullscreenEndDate, setFullscreenEndDate] = useState<string>(endDate);
    const [maxDate, setMaxDate] = useState<string>(
        new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]
    );

    useEffect(() => {
        setIsLoading(true);
        setLoadingStates({
            card1: true,
            card2: true,
            card3: true,
            card4: true
        });

        // Simulate loading for now
        setTimeout(() => {
            setLoadingStates({
                card1: false,
                card2: false,
                card3: false,
                card4: false
            });
            setIsLoading(false);
        }, 1000);
    }, [startDate, endDate]);

    // Fetch the latest date from DataPriceForecast for price analysis
    useEffect(() => {
        const fetchLatestDate = async () => {
            try {
                const response = await getLatestDataPriceForecastDate();
                if (response && response.latest_date) {
                    setMaxDate(response.latest_date);
                }
            } catch (error) {
                console.error("Error fetching latest date:", error);
                // Keep the default maxDate if API call fails
            }
        };
        fetchLatestDate();
    }, []);

    // Check if all data is loaded
    useEffect(() => {
        const allLoaded = !loadingStates.card1 && !loadingStates.card2 && !loadingStates.card3 && !loadingStates.card4;
        setIsLoading(!allLoaded);
    }, [loadingStates]);

    // Listen for fullscreen events from chart components
    useEffect(() => {
        const handleFullscreenEvent = (event: CustomEvent) => {
            handleFullscreen(event.detail);
        };

        window.addEventListener('openFullscreen', handleFullscreenEvent as EventListener);
        return () => {
            window.removeEventListener('openFullscreen', handleFullscreenEvent as EventListener);
        };
    }, []);

    const handleFullscreen = (cardId: string) => {
        setFullscreenCard(cardId);
        // Initialize fullscreen dates with current dashboard dates
        setFullscreenStartDate(startDate);
        setFullscreenEndDate(endDate);
    };

    const handleCloseFullscreen = () => {
        setFullscreenCard(null);
    };

    const handleFullscreenDateChange = (newStartDate: string, newEndDate: string) => {
        setFullscreenStartDate(newStartDate);
        setFullscreenEndDate(newEndDate);
    };

    const getCardTitle = (cardId: string) => {
        switch (cardId) {
            case 'card1': return 'Price Analysis';
            case 'card2': return 'DAM Bids and MCV';
            case 'card3': return 'Consumption & Generation';
            case 'card4': return 'Coal Stock & Price';
            default: return 'Chart';
        }
    };

    const renderFullscreenContent = (cardId: string) => {
        switch (cardId) {
            case 'card1':
                return <PriceAnalysisChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card2':
                return <MarketBiddingVolumeChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card3':
                return <ConsumptionGenerationChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            case 'card4':
                return <CoalStockPriceChartWrapper startDate={fullscreenStartDate} endDate={fullscreenEndDate} isFullscreen={true} />;
            default:
                return null;
        }
    };
    
  return (
    <>
      {isLoading && <Loading />}
      {/* Top Left */}
      <div className="dash-card col-span-1 md:col-span-6">
        <div className="h-full w-full flex flex-col">
          {loadingStates.card1 ? (
            <LoadingSpinner message="" />
          ) : (
            <PriceAnalysisChartWrapper 
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
      {/* Top Right */}
      <div className="dash-card col-span-1 md:col-span-6">
        <div className="h-full w-full flex flex-col">
          {loadingStates.card2 ? (
            <LoadingSpinner message="" />
          ) : (
            <MarketBiddingVolumeChartWrapper 
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
      {/* Bottom Left */}
      <div className="dash-card col-span-1 md:col-span-6">
        <div className="h-full w-full flex flex-col">
          {loadingStates.card3 ? (
            <LoadingSpinner message="" />
          ) : (
            <ConsumptionGenerationChartWrapper 
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
      {/* Bottom Right */}
      <div className="dash-card col-span-1 md:col-span-6">
        <div className="h-full w-full flex flex-col">
          {loadingStates.card4 ? (
            <LoadingSpinner message="" />
          ) : (
            <CoalStockPriceChartWrapper 
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      </div>
      
      {/* Fullscreen Modal */}
      <FullscreenModal
        isOpen={fullscreenCard !== null}
        onClose={handleCloseFullscreen}
        title={fullscreenCard ? getCardTitle(fullscreenCard) : ''}
        showDateControls={true}
        startDate={fullscreenStartDate}
        endDate={fullscreenEndDate}
        onDateChange={handleFullscreenDateChange}
        maxDate={fullscreenCard === 'card1' ? maxDate : undefined}
      >
        {fullscreenCard && renderFullscreenContent(fullscreenCard)}
      </FullscreenModal>
    </>
  );
}

function LoadingSpinner({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

export function MarketDerivativeCard({title, loading}: {title: string, loading?: boolean}) {
    return (
        <div className="text-left h-full w-full flex flex-col">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>
            {loading ? (
                <LoadingSpinner message={`Loading ${title} data...`} />
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <p>Chart will be implemented here</p>
                </div>
            )}
        </div>
    );
}